import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { useMedplum } from "@medplum/react";

import { Box, Flex, Group, Stack } from "@mantine/core";

import { TextWrapper } from "@/components/TextWrapper";
import { useBaselineFhrRefresh } from "@/encounters/components/EncounterLabor/contexts/BaselineFhrRefreshContext";
import {
  FETAL_HEART_RATE_QUESTIONNAIRE_LOAD_ERROR,
  fetchLastAssessedBaselineFetalHeartRate,
  type LastAssessedBaselineFetalHeartRate,
} from "@/encounters/components/EncounterLabor/flowsheet-forms/fetal-heart-rate/utils/last-assessed-baseline-fetal-heart-rate";

interface BaselineData {
  date: string;
  heartRate: number;
  duration: number;
}

export function BaselineCard({ date, heartRate, duration }: BaselineData) {
  return (
    <Flex p="lg" h="auto" w="100%" bg="gray.0" data-testid="baseline-fhr-card">
      <Stack gap={4}>
        <Group>
          <TextWrapper size="sm" variant="bold">
            Baseline assessed:
          </TextWrapper>
          <TextWrapper size="sm" c="gray.9" data-testid="baseline-fhr-date">
            {date}
          </TextWrapper>
        </Group>
        <Group>
          <TextWrapper size="sm" variant="bold">
            Heart rate:
          </TextWrapper>
          <TextWrapper size="sm" c="gray.9" data-testid="baseline-fhr-heart-rate">
            {heartRate} bpm
          </TextWrapper>
        </Group>
        <Group>
          <TextWrapper size="sm" variant="bold">
            Duration of listening:
          </TextWrapper>
          <TextWrapper size="sm" c="gray.9" data-testid="baseline-fhr-duration">
            {duration} seconds
          </TextWrapper>
        </Group>
      </Stack>
    </Flex>
  );
}

interface BaselineFhrProps {
  encounterId?: string;
  /** Refetch when the flowsheet drawer opens so a new baseline entry appears on the next open. */
  refreshWhen?: boolean;
  /** Bump after a successful save so baseline refetches while the drawer stays open. */
  baselineRefreshKey?: number;
}

export function BaselineFhr({
  encounterId: encounterIdProp,
  refreshWhen: refreshWhenProp,
  baselineRefreshKey: baselineRefreshKeyProp,
}: BaselineFhrProps) {
  const refreshContext = useBaselineFhrRefresh();
  const refreshWhen = refreshWhenProp ?? refreshContext.refreshWhen;
  const baselineRefreshKey = baselineRefreshKeyProp ?? refreshContext.baselineRefreshKey;
  const { encounterId: encounterIdFromRoute } = useParams() as { encounterId?: string };
  const encounterId = encounterIdProp ?? encounterIdFromRoute;
  const medplum = useMedplum();
  const [baseline, setBaseline] = useState<LastAssessedBaselineFetalHeartRate | undefined>();
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    if (!refreshWhen || !encounterId) {
      return;
    }

    let isCancelled = false;

    const loadBaseline = async () => {
      setIsLoading(true);
      setLoadError(null);
      try {
        const lastBaseline = await fetchLastAssessedBaselineFetalHeartRate(medplum, encounterId);
        if (!isCancelled) {
          setBaseline(lastBaseline);
        }
      } catch (error) {
        console.error("Error fetching last assessed baseline fetal heart rate:", error);
        if (!isCancelled) {
          setBaseline(undefined);
          const message =
            error instanceof Error ? error.message : "Failed to load last baseline assessment.";
          setLoadError(
            message === FETAL_HEART_RATE_QUESTIONNAIRE_LOAD_ERROR
              ? message
              : "Failed to load last baseline assessment.",
          );
        }
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    };

    void loadBaseline();

    return () => {
      isCancelled = true;
    };
  }, [medplum, encounterId, refreshWhen, baselineRefreshKey]);

  const isInitialLoading = isLoading && baseline === undefined && !loadError;
  if (isInitialLoading) {
    return (
      <Flex p="md" h={43} bg="gray.0" display="flex" align="center" justify="center">
        <TextWrapper size="sm" c="gray.9">
          Loading last baseline…
        </TextWrapper>
      </Flex>
    );
  }

  return (
    <Stack gap={8} data-testid="baseline-fhr-stack">
      {isLoading && baseline !== undefined ? (
        <Flex p="xs" bg="gray.0" display="flex" align="center" justify="center">
          <TextWrapper size="sm" c="gray.7" data-testid="baseline-fhr-refreshing">
            Refreshing last baseline…
          </TextWrapper>
        </Flex>
      ) : null}
      {loadError ? (
        <Flex p="md" h={43} bg="gray.0" display="flex" align="center" justify="center">
          <TextWrapper size="sm" c="red.7" data-testid="baseline-fhr-load-error">
            {loadError}
          </TextWrapper>
        </Flex>
      ) : baseline ? (
        <Box aria-busy={isLoading} style={{ opacity: isLoading ? 0.55 : 1 }}>
          <BaselineCard
            date={baseline.date}
            duration={baseline.duration}
            heartRate={baseline.heartRate}
          />
        </Box>
      ) : (
        <Flex p="md" h={43} bg="gray.0" display="flex" align="center" justify="center">
          <TextWrapper size="sm" c="gray.9">
            No baseline assessments recorded
          </TextWrapper>
        </Flex>
      )}
    </Stack>
  );
}
